import config from '../../config';
import { getNewClubPath } from './clubs';
import {
    ReferrerTypes,
    ReferralApplyErrorTypes,
    REFERRALS_FORBIDDEN_HTTP_STATUS,
} from '../../constants/referrals';
import { buildImportedEventPath, canBuildImportedEventPath } from '../pages/ImportedEvents/importedEventPaths';
import { buildImportedEventTabShareSearch } from '../components/ImportedEvents/importedEventTabParams';

const REFERRAL_LANDING_TYPES = new Set(Object.values(ReferrerTypes));

export function getReferralLandingPath(codeType, code) {
    if (!code || !REFERRAL_LANDING_TYPES.has(codeType)) {
        return null;
    }
    return `/referrals/${codeType}/${code}`;
}

export function getClubReferralURL(clubDetails, currentUser, clubRedirectsEnabled) {
    const clubMemberRole = clubDetails.currentMember
        ? clubDetails.currentMember.role : '';
    const clubTierTypeId = clubDetails.tierTypeId || clubDetails.tierType?.id;
    const newClubPath = getNewClubPath(clubTierTypeId);

    // Owners and Admins share the club referral code
    if (clubMemberRole === 'Owner' || clubMemberRole === 'Admin') {
        if (clubRedirectsEnabled && clubTierTypeId) {
            return `${config.nextAppHost}/${newClubPath}/${clubDetails.id}?_ref=${clubDetails.referralCode}`;
        }
        return `${config.appHost}/clubs/${clubDetails.id}?_ref=${clubDetails.referralCode}`;
    }
    // regular users share their own referral code
    if (clubRedirectsEnabled && clubTierTypeId) {
        return `${config.nextAppHost}/${newClubPath}/${clubDetails.id}?_ref=${currentUser.referralCode}`;
    }
    return `${config.appHost}/clubs/${clubDetails.id}?_ref=${currentUser.referralCode}`;
}

export function getClubInviteURL(clubDetails, user, clubRedirectsEnabled) {
    if (!clubDetails || !user) {
        return '';
    }

    const clubTierTypeId = clubDetails.tierTypeId || clubDetails.tierType?.id;
    if (clubRedirectsEnabled && clubTierTypeId) {
        const newClubPath = getNewClubPath(clubTierTypeId);
        return `${config.nextAppHost}/${newClubPath}/${clubDetails.id}`;
    }

    return `${config.appHost}/clubs/${clubDetails.id}`;
}

export function getEventReferralURL(eventDetails, currentUser) {
    const clubMemberRole = eventDetails.currentMember?.clubMemberRole
        ? eventDetails.currentMember.clubMemberRole.value : '';

    if (clubMemberRole === 'Owner' || clubMemberRole === 'Admin') {
        return `${config.appHost}/events/${eventDetails.id}?_ref=${eventDetails.club.referralCode}&shared=true`;
    }
    return `${config.appHost}/events/${eventDetails.id}?_ref=${currentUser.referralCode}&shared=true`;
}

export function getEventShareURL(eventDetails) {
    return eventDetails ? `${config.appHost}/events/${eventDetails.id}?shared=true` : '';
}

export function getImportedEventShareURL({ importedEventType, eventId, tabId } = {}) {
    if (!canBuildImportedEventPath({ importedEventType, eventId })) {
        return '';
    }
    const path = buildImportedEventPath({ importedEventType, eventId });

    return `${config.appHost}${path}${buildImportedEventTabShareSearch(tabId)}`;
}

export function getHSClaimReferralUrl(referralCode) {
    return `${config.appHost}/hsclub?_ref=${referralCode}`;
}

// POST v2/referrals/apply always resolves 200 with an ApplyReferralCodeResponse
// { success, isEligible, isValidCode, isDisabledReferer, isSelfRefer, offerUnavailable,
// offerUnavailableLocation }. Accepts either that resolved axios response or a rejected axios
// error (network/auth failure) and returns the matching ReferralApplyErrorTypes value, or null
// when the apply succeeded.
//
// Precedence: success, isEligible, isSelfRefer, isDisabledReferer, isValidCode,
// offerUnavailable, offerUnavailableLocation. isSelfRefer is checked before isValidCode so a
// self-refer keeps its own message and analytics (a self-refer response also carries
// isValidCode: false). isDisabledReferer and offerUnavailableLocation both arrive with
// isValidCode: true, so without their own branches they would fall through to the
// invalid-code fallback and mislabel a live code as a bad one.
export function getReferralApplyErrorType(responseOrError) {
    const data = responseOrError?.data ?? responseOrError?.response?.data;

    // `success` is always present on a real apply response, so its absence means the request
    // never reached that verdict (network drop, 401, 5xx, or a rejected model validation).
    // Those say nothing about the code itself, so they must not surface as "that code isn't
    // valid".
    if (typeof data?.success !== 'boolean') {
        return ReferralApplyErrorTypes.REQUEST_FAILED;
    }
    if (data.success) {
        return null;
    }
    if (data.isEligible === false) {
        return ReferralApplyErrorTypes.INELIGIBLE;
    }
    if (data.isSelfRefer) {
        return ReferralApplyErrorTypes.SELF_REFERRAL;
    }
    if (data.isDisabledReferer) {
        return ReferralApplyErrorTypes.DISABLED_REFERER;
    }
    if (data.isValidCode === false) {
        return ReferralApplyErrorTypes.INVALID_CODE;
    }
    if (data.offerUnavailable) {
        return ReferralApplyErrorTypes.OFFER_UNAVAILABLE;
    }
    if (data.offerUnavailableLocation) {
        return ReferralApplyErrorTypes.OFFER_UNAVAILABLE_LOCATION;
    }
    return ReferralApplyErrorTypes.INVALID_CODE;
}

// True when a referral state request failed because the referrer's own code is disabled, which
// both the member and club pages surface as a plain "not available" message rather than a
// retryable error.
export function isReferralsForbidden(error) {
    return error?.response?.status === REFERRALS_FORBIDDEN_HTTP_STATUS;
}
